import { useMemo, useRef, useState } from 'react';
import { Play, ExternalLink, Instagram, Facebook, Music2, Youtube } from 'lucide-react';
import Reveal from './Reveal';

const PLATFORM_META = {
  instagram: { icon: Instagram, label: 'Instagram', color: 'from-fuchsia-500 via-rose-500 to-amber-500' },
  facebook:  { icon: Facebook,  label: 'Facebook',  color: 'from-blue-600 to-blue-500' },
  tiktok:    { icon: Music2,    label: 'TikTok',    color: 'from-slate-900 to-rose-500' },
  youtube:   { icon: Youtube,   label: 'YouTube',   color: 'from-red-600 to-red-500' },
  other:     { icon: Play,      label: 'Video',     color: 'from-cnib-blue to-cnib-navy' },
};

function detectPlatform(url) {
  if (!url) return 'other';
  const u = url.toLowerCase();
  if (u.includes('instagram.com')) return 'instagram';
  if (u.includes('facebook.com') || u.includes('fb.watch')) return 'facebook';
  if (u.includes('tiktok.com')) return 'tiktok';
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  return 'other';
}

export default function ShortsReel({ config }) {
  const items = useMemo(() => {
    const raw = Array.isArray(config?.items) ? config.items.filter((i) => i?.thumbnail && i?.link) : [];
    // Repeat items to simulate infinite scroll (minimum 6 unique → *3 to fill viewport)
    if (raw.length === 0) return [];
    const copies = raw.length < 8 ? 3 : 2;
    const out = [];
    for (let c = 0; c < copies; c++) {
      raw.forEach((it, i) => out.push({ ...it, _k: `${c}-${i}` }));
    }
    return out;
  }, [config?.items]);

  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  if (!config?.enabled || items.length === 0) return null;

  // Duración depende de la cantidad: ~6s por item
  const uniqueCount = (config.items || []).length || 1;
  const duration = Math.max(30, uniqueCount * 6);

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-50 py-16 md:py-20"
      data-testid="home-shorts-reel"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-orange-500">
                {config.eyebrow || 'En redes'}
              </p>
              <h2 className="mt-2 font-heading text-3xl font-extrabold text-cnib-navy md:text-4xl">
                {config.title || 'Shorts, Reels y TikToks'}
              </h2>
              {config.subtitle && <p className="mt-2 max-w-xl text-sm text-slate-500">{config.subtitle}</p>}
            </div>
          </div>
        </Reveal>
      </div>

      <div
        className="group relative mt-10"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Fades laterales */}
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-slate-50 to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-slate-50 to-transparent" />

        <div
          ref={trackRef}
          className="flex w-max gap-5 px-6"
          style={{
            animation: `shorts-marquee ${duration}s linear infinite`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        >
          {items.map((it) => {
            const platform = it.platform || detectPlatform(it.link);
            const meta = PLATFORM_META[platform] || PLATFORM_META.other;
            const Icon = meta.icon;
            return (
              <a
                key={it._k}
                href={it.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`short-card-${it._k}`}
                className="group/card relative block aspect-[9/16] w-32 shrink-0 overflow-hidden rounded-2xl bg-slate-900 shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl sm:w-36 lg:w-40"
              >
                <img
                  src={it.thumbnail}
                  alt={it.title || 'Short'}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover/card:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
                {/* Platform badge */}
                <span
                  className={`absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br ${meta.color} shadow-lg`}
                  aria-label={meta.label}
                >
                  <Icon className="h-3 w-3 text-white" />
                </span>
                {/* Play icon on hover */}
                <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover/card:opacity-100">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/95 shadow-xl ring-4 ring-white/20 backdrop-blur">
                    <Play className="ml-0.5 h-4 w-4 fill-current text-cnib-navy" />
                  </span>
                </span>
                {/* Title at bottom */}
                {it.title && (
                  <div className="absolute inset-x-0 bottom-0 p-2.5">
                    <p className="line-clamp-2 text-[11px] font-bold leading-snug text-white">{it.title}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-slate-300">
                      {meta.label} <ExternalLink className="h-2 w-2" />
                    </p>
                  </div>
                )}
              </a>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes shorts-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}
